import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import styles from './AdDetail.module.css';
import ConfirmDialog from '../components/ConfirmDialog';

const SERVER_URL =
  process.env.NODE_ENV === 'production'
    ? window.location.origin
    : 'http://localhost:3000';
const imgUrl = (p) => p ? `${SERVER_URL}${p.startsWith('/') ? p : `/${p}`}` : '';

export default function AdDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIndex, setImgIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [msgText, setMsgText] = useState('');
  const [msgSaving, setMsgSaving] = useState(false);
  const [msgSaved, setMsgSaved] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  useEffect(() => {
    api.get(`/ads/${id}`)
      .then(r => { setAd(r.data); setMsgText(r.data.adminMessage || ''); })
      .catch(() => alert('Failed to load ad'))
      .finally(() => setLoading(false));
    api.get('/categories')
      .then((r) => setCategories(r.data || []))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!editForm.categoryId) {
      setSubcategories([]);
      return;
    }
    api.get(`/subcategories?categoryId=${editForm.categoryId}`)
      .then((r) => setSubcategories(r.data || []))
      .catch(() => setSubcategories([]));
  }, [editForm.categoryId]);

  const approve = async (action) => {
    const label = action === 'approve' ? 'Approve' : 'Disapprove';
    setConfirm({
      message: `${label} this ad?`,
      onConfirm: async () => {
        setConfirm(null);
        setSaving(true);
        try {
          await api.patch(`/ads/admin/${id}/approve`, { action });
          setAd(prev => ({
            ...prev,
            approvalStatus: action === 'approve' ? 'approved' : 'disapproved',
            isApproved: action === 'approve',
          }));
        } catch { alert('Failed'); }
        finally { setSaving(false); }
      },
    });
  };

  const toggle = async () => {
    const action = ad.isActive ? 'Deactivate' : 'Activate';
    setConfirm({
      message: `${action} this ad?`,
      onConfirm: async () => {
        setConfirm(null);
        setSaving(true);
        try {
          const { data } = await api.patch(`/ads/admin/${id}/toggle`);
          setAd(prev => ({ ...prev, isActive: data.isActive }));
        } catch { alert('Failed'); }
        finally { setSaving(false); }
      },
    });
  };

  const remove = async () => {
    setConfirm({
      message: 'Delete this ad? This cannot be undone.',
      onConfirm: async () => {
        setConfirm(null);
        try { await api.delete(`/ads/admin/${id}`); navigate('/ads'); }
        catch { alert('Delete failed'); }
      },
    });
  };

  const saveMessage = async () => {
    setMsgSaving(true);
    setMsgSaved(false);
    try {
      await api.patch(`/ads/admin/${id}/message`, { adminMessage: msgText });
      setMsgSaved(true);
      setTimeout(() => setMsgSaved(false), 2500);
    } catch { alert('Failed to save message'); }
    finally { setMsgSaving(false); }
  };

  const startEdit = () => {
    setEditError('');
    setEditForm({
      title: ad.title || '',
      brand: ad.brand || '',
      price: ad.price != null ? String(ad.price) : '',
      description: ad.description || '',
      city: ad.city || '',
      area: ad.area || '',
      address: ad.address || '',
      state: ad.state || '',
      categoryId: ad.category?._id || ad.category || '',
      subcategoryId: ad.subcategory?._id || ad.subcategory || '',
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditError('');
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.categoryId) {
      setEditError('Category is required');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      const { data } = await api.put(`/ads/admin/${id}`, {
        title: editForm.title.trim(),
        brand: editForm.brand.trim(),
        price: editForm.price,
        description: editForm.description.trim(),
        city: editForm.city.trim(),
        area: editForm.area.trim(),
        address: editForm.address.trim(),
        state: editForm.state.trim(),
        category: editForm.categoryId,
        subcategory: editForm.subcategoryId || null,
      });
      setAd(data);
      setEditing(false);
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) return <div className={styles.center}>Loading...</div>;
  if (!ad) return <div className={styles.center}>Ad not found.</div>;

  const images = ad.images || [];
  const attrs = ad.attributes || {};

  return (
    <div className={styles.page}>
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
      {/* Back */}
      <button className={styles.backBtn} onClick={() => navigate('/ads')}>← Back to Ads</button>

      <div className={styles.layout}>
        {/* Left — images + actions */}
        <div className={styles.left}>
          {/* Image viewer */}
          <div className={styles.imgBox}>
            {images.length > 0
              ? <img src={imgUrl(images[imgIndex])} alt={ad.title} className={styles.mainImg} />
              : <div className={styles.noImg}>No Images</div>}
          </div>
          {images.length > 1 && (
            <div className={styles.thumbRow}>
              {images.map((img, i) => (
                <img
                  key={i}
                  src={imgUrl(img)}
                  alt=""
                  className={`${styles.thumb} ${i === imgIndex ? styles.thumbActive : ''}`}
                  onClick={() => setImgIndex(i)}
                />
              ))}
            </div>
          )}

          {/* Action buttons */}
          <div className={styles.actions}>
            {!editing && (
              <button className={styles.toggleOnBtn} onClick={startEdit}>✎ Edit Ad</button>
            )}
            {ad.approvalStatus === 'approved' ? (
              <button className={styles.disapproveBtn} disabled={saving || editing} onClick={() => approve('disapprove')}>✗ Disapprove</button>
            ) : (
              <button className={styles.approveBtn} disabled={saving || editing} onClick={() => approve('approve')}>✓ Approve</button>
            )}
            <button
              className={ad.isActive ? styles.toggleOffBtn : styles.toggleOnBtn}
              disabled={saving || editing}
              onClick={toggle}
            >
              {ad.isActive ? '⏸ Deactivate' : '▶ Activate'}
            </button>
            <button className={styles.deleteBtn} disabled={editing} onClick={remove}>🗑 Delete</button>
            <button
              className={ad.isFeatured ? styles.toggleOffBtn : styles.toggleOnBtn}
              disabled={saving || editing}
              onClick={async () => {
                setSaving(true);
                try {
                  const { data } = await api.patch(`/ads/admin/${id}/feature`);
                  setAd((prev) => ({
                    ...prev,
                    isFeatured: data.isFeatured,
                    featuredAt: data.featuredAt,
                  }));
                } catch {
                  alert('Failed');
                } finally {
                  setSaving(false);
                }
              }}
            >
              {ad.isFeatured ? '★ Unfeature' : '☆ Feature'}
            </button>
          </div>
        </div>

        {/* Right — details */}
        <div className={styles.right}>
          {/* Status badges */}
          <div className={styles.badges}>
            <span className={ad.isActive ? styles.badgeActive : styles.badgeInactive}>
              {ad.isActive ? 'Active' : 'Inactive'}
            </span>
            <span className={
              ad.approvalStatus === 'approved' ? styles.badgeActive :
              ad.approvalStatus === 'disapproved' ? styles.badgeInactive :
              styles.badgePending
            }>
              {ad.approvalStatus || 'Pending'}
            </span>
          </div>

          {editing ? (
            <form className={styles.editForm} onSubmit={saveEdit}>
              <h2 className={styles.editHeading}>Edit Ad</h2>
              {editError && <div className={styles.editError}>{editError}</div>}
              <label>Category *</label>
              <select
                value={editForm.categoryId || ''}
                onChange={(e) => setEditForm({
                  ...editForm,
                  categoryId: e.target.value,
                  subcategoryId: '',
                })}
                required
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
              <label>Subcategory</label>
              <select
                value={editForm.subcategoryId || ''}
                onChange={(e) => setEditForm({ ...editForm, subcategoryId: e.target.value })}
                disabled={!editForm.categoryId}
              >
                <option value="">None</option>
                {subcategories.map((s) => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
              <label>Title *</label>
              <input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                required
              />
              <label>Brand</label>
              <input
                value={editForm.brand}
                onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })}
              />
              <label>Price (₹) *</label>
              <input
                type="number"
                min="0"
                step="any"
                value={editForm.price}
                onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                required
              />
              <label>Description *</label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={5}
                required
              />
              <label>City</label>
              <input
                value={editForm.city}
                onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
              />
              <label>Area</label>
              <input
                value={editForm.area}
                onChange={(e) => setEditForm({ ...editForm, area: e.target.value })}
              />
              <label>Address</label>
              <input
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
              />
              <label>State</label>
              <input
                value={editForm.state}
                onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
              />
              <div className={styles.editActions}>
                <button type="button" className={styles.cancelEditBtn} onClick={cancelEdit}>Cancel</button>
                <button type="submit" className={styles.saveEditBtn} disabled={editSaving}>
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <>
              <h1 className={styles.title}>{ad.title}</h1>
              <div className={styles.price}>₹{Number(ad.price).toLocaleString('en-PK')}</div>

              {/* Core info */}
              <div className={styles.section}>
                <div className={styles.sectionTitle}>Details</div>
                <div className={styles.grid}>
                  <Row label="Category"    value={ad.category?.name} />
                  <Row label="Subcategory" value={ad.subcategory?.name} />
                  {ad.brand && <Row label="Brand" value={ad.brand} />}
                  <Row label="City"    value={ad.city} />
                  <Row label="Area"    value={ad.area} />
                  <Row label="Address" value={ad.address} />
                  <Row label="State"   value={ad.state} />
                  <Row label="Posted"  value={new Date(ad.createdAt).toLocaleString('en-PK', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })} />
                  <Row label="Views" value={String(ad.views ?? 0)} />
                  {ad.expiresAt && <Row label="Expires" value={new Date(ad.expiresAt).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' })} />}
                  {ad.bumpedAt && <Row label="Last Bumped" value={new Date(ad.bumpedAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} />}
                </div>
              </div>

              {/* Description */}
              {ad.description && (
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Description</div>
                  <p className={styles.desc}>{ad.description}</p>
                </div>
              )}
            </>
          )}

          {/* Dynamic attributes */}
          {Object.keys(attrs).length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Specifications</div>
              <div className={styles.grid}>
                {Object.entries(attrs).map(([k, v]) => (
                  <Row key={k} label={k.replace(/_/g, ' ')} value={v} />
                ))}
              </div>
            </div>
          )}

          {/* Seller */}
          {ad.user && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Posted By</div>
              <div className={styles.sellerBox}>
                <div className={styles.avatar}>
                  {ad.user.avatar
                    ? <img src={imgUrl(ad.user.avatar)} alt="" />
                    : <span>{(ad.user.name || ad.user.email || '?')[0].toUpperCase()}</span>}
                </div>
                <div>
                  <div className={styles.sellerName}>{ad.user.name || '—'}</div>
                  <div className={styles.sellerEmail}>{ad.user.email}</div>
                  {ad.user.phone && <div className={styles.sellerEmail}>{ad.user.phone}</div>}
                </div>
              </div>
            </div>
          )}

          {/* Admin Message */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Admin Message to User</div>
            <textarea
              className={styles.msgTextarea}
              placeholder="e.g. Your ad was disapproved because the price is missing. Please edit and resubmit."
              value={msgText}
              onChange={e => { setMsgText(e.target.value); setMsgSaved(false); }}
              rows={4}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
              <button className={styles.saveMsgBtn} onClick={saveMessage} disabled={msgSaving}>
                {msgSaving ? 'Saving...' : 'Save Message'}
              </button>
              {msgSaved && <span style={{ color: '#6CA651', fontSize: 13, fontWeight: 600 }}>✓ Saved</span>}
            </div>
          </div>

          {/* Location */}
          {ad.location?.coordinates && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Location</div>
              <a
                className={styles.mapLink}
                href={`https://maps.google.com/?q=${ad.location.coordinates[1]},${ad.location.coordinates[0]}`}
                target="_blank"
                rel="noreferrer"
              >
                📍 View on Google Maps ({ad.location.coordinates[1].toFixed(5)}, {ad.location.coordinates[0].toFixed(5)})
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue}>{value}</span>
    </div>
  );
}
